/**
 * Panel Component
 *
 * Main UI component for the Data Notebook panel.
 * Manages panel state, rendering, and user interactions.
 */

import * as duckdb from '@duckdb/duckdb-wasm';
import { createPanelHost, getPanelHost, type PanelHost } from './panel-host';
import { panelStyles } from './panel-styles';
import { createShell, type ShellInstance } from './shell';
import type { Position, Theme, PanelState } from '../types/config';

export type PanelTab = 'repl' | 'sql' | 'datasets';

export interface PanelOptions {
  position: Position;
  theme: Theme;
  initialState: PanelState;
  onClose?: () => void;
  onMinimize?: () => void;
  onExpand?: () => void;
  onRunQuery?: (sql: string) => void;
}

export interface Panel {
  /** Current panel state */
  state: PanelState;
  /** Show the panel */
  show(): void;
  /** Hide the panel */
  hide(): void;
  /** Toggle panel visibility */
  toggle(): void;
  /** Minimize the panel to badge */
  minimize(): void;
  /** Expand from minimized state */
  expand(): void;
  /** Update theme */
  setTheme(theme: Theme): void;
  /** Update position */
  setPosition(position: Position): void;
  /** Update table count in status */
  setTableCount(count: number): void;
  /** Set status message */
  setStatus(message: string, type?: 'ready' | 'loading' | 'error'): void;
  /** Get the DuckDB database instance (may be null if not initialized) */
  getDatabase(): duckdb.AsyncDuckDB | null;
  /** Destroy the panel */
  destroy(): Promise<void>;
}

/**
 * Create the panel UI
 */
export function createPanel(options: PanelOptions): Panel {
  const host = createPanelHost();
  let state: PanelState = options.initialState;
  let currentTab: PanelTab = 'repl';
  let tableCount = 0;
  let statusMessage = 'Ready';
  let statusType: 'ready' | 'loading' | 'error' = 'ready';
  let currentTheme = options.theme;
  let currentPosition = options.position;

  // Shell instance for REPL
  const shellInstance: ShellInstance = createShell();
  let shellInitPromise: Promise<void> | null = null;

  // Inject styles
  host.injectStyles(panelStyles);

  // Initial render
  render();

  // Bind events
  bindEvents(host);

  function render(): void {
    const themeClass = currentTheme === 'light' ? 'light' : '';
    const container = host.shadowRoot.getElementById('dn-content');
    if (container) {
      // Update host class for theme
      if (themeClass) {
        host.hostElement.classList.add('light');
      } else {
        host.hostElement.classList.remove('light');
      }
    }

    host.render(`
      <!-- Main Panel -->
      <div class="dn-panel ${currentPosition} ${state === 'hidden' ? 'hidden' : ''}"
           style="${state === 'minimized' ? 'display: none;' : ''}">
        <!-- Header -->
        <div class="dn-header">
          <div class="dn-header-title">
            <span class="dn-header-icon">&#128450;</span>
            <span>Data Notebook</span>
          </div>
          <div class="dn-header-controls">
            <button class="dn-header-btn minimize" title="Minimize" data-action="minimize">
              &#8722;
            </button>
            <button class="dn-header-btn close" title="Close" data-action="close">
              &#10005;
            </button>
          </div>
        </div>

        <!-- Tabs -->
        <div class="dn-tabs">
          <button class="dn-tab ${currentTab === 'repl' ? 'active' : ''}" data-tab="repl">
            <span class="dn-tab-icon">&gt;_</span>
            REPL
          </button>
          <button class="dn-tab ${currentTab === 'sql' ? 'active' : ''}" data-tab="sql">
            <span class="dn-tab-icon">&#9654;</span>
            SQL
          </button>
          <button class="dn-tab ${currentTab === 'datasets' ? 'active' : ''}" data-tab="datasets">
            <span class="dn-tab-icon">&#128202;</span>
            Datasets
          </button>
        </div>

        <!-- Content -->
        <div class="dn-content">
          <!-- REPL Tab -->
          <div class="dn-tab-content ${currentTab === 'repl' ? 'active' : ''}" data-content="repl">
            <div class="dn-repl-container" id="dn-shell-container"></div>
          </div>

          <!-- SQL Tab -->
          <div class="dn-tab-content ${currentTab === 'sql' ? 'active' : ''}" data-content="sql">
            <div class="dn-sql-input-container">
              <textarea
                class="dn-sql-input"
                placeholder="SELECT * FROM my_table LIMIT 10;"
                spellcheck="false"
              ></textarea>
              <div class="dn-sql-actions">
                <button class="dn-run-btn" data-action="run-query">
                  <span>&#9654;</span>
                  Run Query
                </button>
                <span class="dn-shortcut-hint">Cmd/Ctrl + Enter</span>
              </div>
            </div>
            <div class="dn-results">
              <div class="dn-results-placeholder">
                <div class="dn-results-placeholder-icon">&#128269;</div>
                <div class="dn-results-placeholder-text">
                  Run a query to see results
                </div>
              </div>
            </div>
          </div>

          <!-- Datasets Tab -->
          <div class="dn-tab-content ${currentTab === 'datasets' ? 'active' : ''}" data-content="datasets">
            <div class="dn-datasets">
              ${tableCount === 0 ? `
                <div class="dn-datasets-empty">
                  <div class="dn-datasets-empty-icon">&#128451;</div>
                  <div class="dn-datasets-empty-text">No datasets loaded</div>
                  <div class="dn-datasets-empty-hint">
                    CSV data will appear here when intercepted
                  </div>
                </div>
              ` : `
                <div class="dn-dataset-item">
                  <div class="dn-dataset-info">
                    <div class="dn-dataset-name">sample_data</div>
                    <div class="dn-dataset-meta">1,234 rows &bull; 5 columns</div>
                  </div>
                </div>
              `}
            </div>
          </div>
        </div>

        <!-- Status Bar -->
        <div class="dn-status">
          <div class="dn-status-left">
            <div class="dn-status-indicator ${statusType}"></div>
            <span>${statusMessage}</span>
          </div>
          <span>${tableCount} table${tableCount !== 1 ? 's' : ''} loaded</span>
        </div>
      </div>

      <!-- Minimized Badge -->
      <div class="dn-badge ${currentPosition} ${state === 'minimized' ? '' : 'hidden'}" data-action="expand">
        <span class="dn-badge-icon">&#128450;</span>
        <span>Data Notebook</span>
        ${tableCount > 0 ? `<span class="dn-badge-count">${tableCount}</span>` : ''}
      </div>
    `);

    // Rebind events after render
    bindEvents(host);
  }

  function bindEvents(host: PanelHost): void {
    const shadowRoot = host.shadowRoot;

    // Tab switching
    shadowRoot.querySelectorAll('.dn-tab').forEach((tab) => {
      tab.addEventListener('click', (e) => {
        const tabName = (e.currentTarget as HTMLElement).dataset.tab as PanelTab;
        if (tabName && tabName !== currentTab) {
          currentTab = tabName;
          if (tabName === 'repl') {
            initializeShellIfNeeded();
          } else {
            render();
          }
        }
      });
    });

    // Header buttons
    shadowRoot.querySelectorAll('[data-action]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const action = (e.currentTarget as HTMLElement).dataset.action;
        switch (action) {
          case 'close':
            hide();
            options.onClose?.();
            break;
          case 'minimize':
            minimize();
            options.onMinimize?.();
            break;
          case 'expand':
            expand();
            options.onExpand?.();
            break;
          case 'run-query':
            handleRunQuery();
            break;
        }
      });
    });

    // SQL input keyboard shortcuts
    const sqlInput = shadowRoot.querySelector('.dn-sql-input') as HTMLTextAreaElement;
    if (sqlInput) {
      sqlInput.addEventListener('keydown', (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
          e.preventDefault();
          handleRunQuery();
        }
      });
    }
  }

  async function handleRunQuery(): Promise<void> {
    const sqlInput = host.shadowRoot.querySelector('.dn-sql-input') as HTMLTextAreaElement;
    const resultsContainer = host.shadowRoot.querySelector('.dn-results') as HTMLElement;

    if (!sqlInput || !sqlInput.value.trim()) {
      return;
    }

    const sql = sqlInput.value.trim();

    // Notify callback
    options.onRunQuery?.(sql);

    // Check if shell is initialized
    if (!shellInstance.isInitialized) {
      renderResultsError(resultsContainer, 'Database not initialized. Open the REPL tab first to initialize.');
      return;
    }

    // Show loading state
    renderResultsLoading(resultsContainer);

    try {
      const result = await shellInstance.executeQuery(sql);

      if (result.error) {
        renderResultsError(resultsContainer, result.error);
      } else if (result.columns.length > 0) {
        renderResultsTable(resultsContainer, result);
      } else {
        renderResultsSuccess(resultsContainer, 'Query executed successfully.');
      }
    } catch (err: any) {
      renderResultsError(resultsContainer, err.message || 'Query execution failed');
    }
  }

  function renderResultsLoading(container: HTMLElement): void {
    container.innerHTML = `
      <div class="dn-results-placeholder">
        <div class="dn-repl-loading-spinner"></div>
        <div class="dn-results-placeholder-text">Executing query...</div>
      </div>
    `;
  }

  function renderResultsError(container: HTMLElement, message: string): void {
    container.innerHTML = `
      <div class="dn-results-error">
        <div class="dn-results-error-icon">&#9888;</div>
        <div class="dn-results-error-text">${escapeHtml(message)}</div>
      </div>
    `;
  }

  function renderResultsSuccess(container: HTMLElement, message: string): void {
    container.innerHTML = `
      <div class="dn-results-success">
        <div class="dn-results-success-icon">&#10003;</div>
        <div class="dn-results-success-text">${escapeHtml(message)}</div>
      </div>
    `;
  }

  function renderResultsTable(container: HTMLElement, result: { columns: string[]; rows: any[][]; rowCount: number }): void {
    let tableHtml = `
      <div class="dn-results-table-wrapper">
        <table class="dn-results-table">
          <thead>
            <tr>
              ${result.columns.map(col => `<th>${escapeHtml(col)}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${result.rows.map(row => `
              <tr>
                ${row.map(cell => `<td>${cell === null ? '<span class="dn-null">NULL</span>' : escapeHtml(String(cell))}</td>`).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="dn-results-row-count">${result.rowCount} row${result.rowCount !== 1 ? 's' : ''}</div>
      </div>
    `;
    container.innerHTML = tableHtml;
  }

  function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  async function initializeShellIfNeeded(): Promise<void> {
    // Render first to show the container
    render();

    if (shellInstance.isInitialized || shellInitPromise) {
      return shellInitPromise || Promise.resolve();
    }

    const container = host.shadowRoot.getElementById('dn-shell-container');
    if (!container) {
      console.error('[DataNotebook] Shell container not found');
      return;
    }

    // Initialize shell (basic shell only needs container, xterm shell needs shadowRoot too)
    shellInitPromise = shellInstance.initialize(container).catch((error) => {
      console.error('[DataNotebook] Shell initialization failed:', error);
      // Error is already displayed by shell
    });

    return shellInitPromise;
  }

  function show(): void {
    state = 'expanded';
    render();
    // Initialize shell if REPL tab is active
    if (currentTab === 'repl') {
      initializeShellIfNeeded();
    }
  }

  function hide(): void {
    state = 'hidden';
    render();
  }

  function toggle(): void {
    if (state === 'hidden') {
      show();
    } else if (state === 'expanded') {
      hide();
    } else if (state === 'minimized') {
      expand();
    }
  }

  function minimize(): void {
    state = 'minimized';
    render();
  }

  function expand(): void {
    state = 'expanded';
    render();
  }

  return {
    get state() {
      return state;
    },
    show,
    hide,
    toggle,
    minimize,
    expand,
    setTheme(theme: Theme): void {
      currentTheme = theme;
      render();
    },
    setPosition(position: Position): void {
      currentPosition = position;
      render();
    },
    setTableCount(count: number): void {
      tableCount = count;
      render();
    },
    setStatus(message: string, type: 'ready' | 'loading' | 'error' = 'ready'): void {
      statusMessage = message;
      statusType = type;
      render();
    },
    getDatabase(): duckdb.AsyncDuckDB | null {
      return shellInstance.getDatabase();
    },
    async destroy(): Promise<void> {
      await shellInstance.destroy();
      host.destroy();
    },
  };
}
